import React, { useState, useEffect } from 'react';
import { useModalDismiss } from '../../hooks/useModalDismiss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileAlt, faVolumeUp, faSearch, faChevronLeft, faChevronRight, faFileExcel } from '@fortawesome/free-solid-svg-icons';
import axios from '../../Api';
import { useAuth } from '../../contexts/AuthContext';
import ConfirmationModal from '../../components/ConfirmationModal';
import SuccessModal from '../../components/SuccessModal';

const QualityBatchesList = () => {
  const { user } = useAuth();
  const isReadOnly = user?.role === 'chef_terrain_superieur';
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [surveyFilter, setSurveyFilter] = useState('all');
  const [surveys, setSurveys] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [selectedBatch, setSelectedBatch] = useState(null);
  const selectedBatchDismiss = useModalDismiss(() => setSelectedBatch(null), { backdropClose: false, enabled: selectedBatch });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalBatches, setTotalBatches] = useState(0);
  const [exportError, setExportError] = useState(null);
  const exportErrorDismiss = useModalDismiss(() => setExportError(null), { backdropClose: false, enabled: exportError });
  const [showEscalateModal, setShowEscalateModal] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [batchToAction, setBatchToAction] = useState(null);
  const [processingAction, setProcessingAction] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const batchesPerPage = 10;

  useEffect(() => {
    fetchSurveys();
  }, []);

  useEffect(() => {
    fetchBatches();
  }, [currentPage, statusFilter, surveyFilter, searchTerm, dateFilter]);

  const fetchSurveys = async () => {
    try {
      const response = await axios.get('/control/surveys', {
        params: {
          per_page: 1000 // Get all surveys for the filter dropdown
        }
      });
      // Handle both paginated and non-paginated responses
      if (response.data.data && Array.isArray(response.data.data)) {
        setSurveys(response.data.data);
      } else if (Array.isArray(response.data)) {
        setSurveys(response.data);
      } else {
        setSurveys([]);
      }
    } catch (error) {
      console.error('Error fetching surveys:', error);
      setSurveys([]);
    }
  };

  const fetchBatches = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        per_page: batchesPerPage
      };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (surveyFilter !== 'all') params.survey_id = surveyFilter;
      if (searchTerm) params.search = searchTerm;
      if (dateFilter) params.date = dateFilter;
      const response = await axios.get('/control/quality-batches', { params });
      setBatches(response.data.data || []);
      setTotalPages(response.data.last_page || 1);
      setTotalBatches(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching batches:', error);
      alert('Erreur lors du chargement des lots');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  const handleSurveyFilter = (surveyId) => {
    setSurveyFilter(surveyId);
    setCurrentPage(1);
  };

  const handleDateFilter = (date) => {
    setDateFilter(date);
    setCurrentPage(1);
  };

  const handleSearch = () => {
    setSearchTerm(searchQuery);
    setCurrentPage(1);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleExport = async () => {
    try {
      setExportError(null);
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (surveyFilter !== 'all') params.survey_id = surveyFilter;
      if (searchTerm) params.search = searchTerm;
      if (dateFilter) params.date = dateFilter;

      const response = await axios.get('/control/quality-batches/export', {
        params,
        responseType: 'blob',
        validateStatus: function (status) {
          // Don't throw error for 404, we'll handle it manually
          return status >= 200 && status < 500;
        }
      });

      // Check if response is an error (404 or other error status)
      if (response.status === 404 || response.status >= 400) {
        // Try to parse error message from blob
        try {
          const text = await response.data.text();
          const errorData = JSON.parse(text);
          setExportError(errorData.message || 'Aucune donnée à exporter pour les filtres sélectionnés.');
        } catch (parseError) {
          // If parsing fails, use default message
          setExportError('Aucune donnée à exporter pour les filtres sélectionnés.');
        }
        return;
      }

      // Check if response is actually JSON (error) instead of Excel file
      const contentType = response.headers['content-type'] || '';
      if (contentType.includes('application/json')) {
        try {
          const text = await response.data.text();
          const errorData = JSON.parse(text);
          setExportError(errorData.message || 'Aucune donnée à exporter pour les filtres sélectionnés.');
        } catch (parseError) {
          setExportError('Aucune donnée à exporter pour les filtres sélectionnés.');
        }
        return;
      }

      // Create download link for successful Excel file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'lots_qualite_' + new Date().toISOString().split('T')[0] + '.xlsx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting batches:', error);
      
      // Try to extract error message from response
      let errorMessage = 'Erreur lors de l\'exportation. Veuillez réessayer.';
      
      if (error.response) {
        // Check if it's a 404 (no data to export)
        if (error.response.status === 404) {
          // If response is blob, try to parse it
          if (error.response.data instanceof Blob) {
            try {
              const text = await error.response.data.text();
              const errorData = JSON.parse(text);
              setExportError(errorData.message || 'Aucune donnée à exporter pour les filtres sélectionnés.');
            } catch (parseError) {
              setExportError('Aucune donnée à exporter pour les filtres sélectionnés.');
            }
            return;
          } else if (error.response.data && error.response.data.message) {
            errorMessage = error.response.data.message;
          } else {
            errorMessage = 'Aucune donnée à exporter pour les filtres sélectionnés.';
          }
        } else if (error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      }
      
      setExportError(errorMessage);
    }
  };

  const handleEscalateClick = (batchId) => {
    setBatchToAction(batchId);
    setShowEscalateModal(true);
  };

  const handleEscalate = async () => {
    if (!batchToAction) return;

    try {
      setProcessingAction(true);
      await axios.post(`/control/quality-batches/${batchToAction}/escalate`);
      setShowEscalateModal(false);
      setSuccessMessage('Lot escaladé avec succès');
      setShowSuccessModal(true);
      fetchBatches();
    } catch (error) {
      console.error('Error escalating batch:', error);
      setShowEscalateModal(false);
      setSuccessMessage('Erreur lors de l\'escalade');
      setShowSuccessModal(true);
    } finally {
      setProcessingAction(false);
      setBatchToAction(null);
    }
  };

  const handleVerifyClick = (batchId) => {
    setBatchToAction(batchId);
    setShowVerifyModal(true);
  };

  const handleVerify = async () => {
    if (!batchToAction) return;

    try {
      setProcessingAction(true);
      await axios.post(`/control/quality-batches/${batchToAction}/verify`);
      setShowVerifyModal(false);
      setSuccessMessage('Lot marqué comme vérifié');
      setShowSuccessModal(true);
      fetchBatches();
    } catch (error) {
      console.error('Error verifying batch:', error);
      setShowVerifyModal(false);
      setSuccessMessage('Erreur lors de la vérification');
      setShowSuccessModal(true);
    } finally {
      setProcessingAction(false);
      setBatchToAction(null);
    }
  };

  const handleViewDetails = async (batchId) => {
    try {
      const response = await axios.get(`/control/quality-batches/${batchId}`);
      setSelectedBatch(response.data);
    } catch (error) {
      console.error('Error fetching batch details:', error);
      alert('Erreur lors du chargement des détails');
    }
  };

  const getStatusBadge = (status, isSurveyEnabled = true) => {
    const badges = {
      pending: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'En attente' },
      in_progress: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'En cours' },
      passed: { bg: 'bg-green-100', text: 'text-green-800', label: 'Réussi' },
      failed: { bg: 'bg-red-100', text: 'text-red-800', label: 'Échoué' },
      escalated: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Escaladé' }
    };
    const badge = badges[status] || badges.pending;
    
    // If survey is disabled and batch is active, show paused indicator
    if (!isSurveyEnabled && (status === 'pending' || status === 'in_progress')) {
      return (
        <div className="flex items-center gap-2">
          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${badge.bg} ${badge.text}`}>
            {badge.label}
          </span>
          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
            ⏸ Enquête Désactivée
          </span>
        </div>
      );
    }
    
    return (
      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  return (
    <div className="container mx-auto px-4 py-4 sm:py-8">
      <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Lots de Contrôle Qualité</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-2">
            Chaque lot contient <strong>5 questionnaires</strong> (submissions) d'un enquêteur. Créé automatiquement pour les enquêteurs avec score &lt; 70%
          </p>
        </div>
        <button
          onClick={handleExport}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2 w-full sm:w-auto"
          title="Exporter en Excel"
        >
          <FontAwesomeIcon icon={faFileExcel} />
          <span>Exporter en Excel</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow mb-4 sm:mb-6 p-4 sm:p-6">
        <div className="space-y-4">
          {/* Status Filters */}
          <div className="flex gap-2 flex-wrap">
            {['all', 'in_progress', 'passed', 'failed', 'escalated'].map((status) => (
              <button
                key={status}
                onClick={() => handleStatusFilter(status)}
                className={`px-3 sm:px-4 py-2 text-xs sm:text-sm rounded transition-colors ${
                  statusFilter === status
                    ? 'bg-red-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {status === 'all' ? 'Tous' : 
                 status === 'in_progress' ? 'En cours' :
                 status === 'passed' ? 'Réussis' :
                 status === 'failed' ? 'Échoués' :
                 'Escaladés'}
              </button>
            ))}
          </div>
          
          {/* Search, Survey and Date Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 w-full min-w-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">Recherche</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Rechercher par contrôleur, soumission ID, LimeSurvey ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="flex-1 min-w-0 h-10 px-3 text-sm border border-red-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                />
                <button
                  onClick={handleSearch}
                  className="h-10 px-3 sm:px-4 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors flex items-center justify-center flex-shrink-0"
                  title="Rechercher"
                >
                  <FontAwesomeIcon icon={faSearch} />
                </button>
              </div>
            </div>
            <div className="w-full sm:w-auto">
              <label className="block text-sm font-medium text-gray-700 mb-1">Enquête</label>
              <select
                value={surveyFilter}
                onChange={(e) => handleSurveyFilter(e.target.value)}
                className="w-full sm:w-auto h-10 px-3 text-sm border border-red-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 min-w-[200px]"
              >
                <option value="all">Toutes</option>
                {surveys.map((survey) => (
                  <option key={survey.id} value={survey.id}>
                    {survey.title}
                  </option>
                ))}
              </select>
            </div>
            <div className="w-full sm:w-auto">
              <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
              <input
                type="date"
                value={dateFilter}
                onChange={(e) => handleDateFilter(e.target.value)}
                className="w-full sm:w-auto h-10 px-3 text-sm border border-red-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Batches Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {/* Desktop Table View */}
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ID
                </th>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Enquêteur
                </th>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Lot N° / Enquête
                </th>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contrôleurs Assignés
                </th>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Résultats
                </th>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {batches.map((batch) => (
                <tr key={batch.id} className="hover:bg-gray-50">
                  <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {batch.id}
                  </td>
                  <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {batch.interviewer?.name || batch.supervisor?.name || 'N/A'}
                    </div>
                    <div className="text-xs text-gray-500">
                      Ref: {batch.interviewer?.ref || batch.supervisor?.ref || 'N/A'}
                    </div>
                    {batch.supervisor_id && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 mt-1">
                        Prestataire
                      </span>
                    )}
                  </td>
                  <td className="px-4 lg:px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">
                      Lot N° {batch.batch_number}
                    </div>
                    {(batch.survey || (batch.assignments && batch.assignments.length > 0 && batch.assignments[0]?.survey)) && (
                      <>
                        {batch.survey ? (
                          <>
                            <div className="text-sm text-gray-700 mt-1">
                              {batch.survey.title || batch.survey.name || 'N/A'}
                            </div>
                            <div className="text-xs text-gray-500">
                              ID: {batch.survey.limesurvey_id || batch.survey.id || 'N/A'}
                            </div>
                          </>
                        ) : batch.assignments && batch.assignments[0]?.survey ? (
                          <>
                            <div className="text-sm text-gray-700 mt-1">
                              {batch.assignments[0].survey.title || batch.assignments[0].survey.name || 'N/A'}
                            </div>
                            <div className="text-xs text-gray-500">
                              ID: {batch.assignments[0].survey.limesurvey_id || batch.assignments[0].survey.id || 'N/A'}
                            </div>
                          </>
                        ) : null}
                      </>
                    )}
                  </td>
                  <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                    {batch.assigned_controllers && batch.assigned_controllers.length > 0 ? (
                      <div className="space-y-1">
                        {batch.assigned_controllers.map((qc, index) => (
                          <div key={qc.id || index} className="text-sm">
                            <div className="font-medium text-gray-900">
                              {qc.controller?.name || 'N/A'}
                            </div>
                            <div className="text-xs text-gray-500">
                              Ref: {qc.controller?.ref || 'N/A'}
                            </div>
                            {qc.assigned_assignments_count > 0 && (
                              <div className="text-xs text-blue-600">
                                {qc.assigned_assignments_count} questionnaire(s)
                              </div>
                            )}
                          </div>
                        ))}
                        {batch.assigned_at && (
                          <div className="text-xs text-gray-400 mt-1">
                            Assigné: {new Date(batch.assigned_at).toLocaleDateString('fr-FR')}
                          </div>
                        )}
                      </div>
                    ) : batch.assigned_controller ? (
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {batch.assigned_controller.name}
                        </div>
                        <div className="text-xs text-gray-500">
                          Ref: {batch.assigned_controller.ref}
                        </div>
                        {batch.assigned_at && (
                          <div className="text-xs text-gray-400">
                            Assigné: {new Date(batch.assigned_at).toLocaleDateString('fr-FR')}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400 italic">Non assigné</span>
                    )}
                  </td>
                  <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col gap-1 items-start">
                      {getStatusBadge(batch.status, batch.is_survey_enabled)}
                      {batch.status === 'escalated' && batch.escalated_verif && (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          ✓ Vérifié
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-green-600 text-xs">
                          {batch.validated_count || 0} / {batch.total_count} validés
                        </span>
                      </div>
                      {batch.done_count !== undefined && batch.done_count > 0 && (
                        <span className="text-xs text-gray-500">
                          {batch.done_count} / {batch.total_count} contrôlés
                        </span>
                      )}
                    </div>
                    {batch.total_count > 0 && (
                      <div className="mt-1 w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-red-600 h-2 rounded-full transition-all" 
                          style={{ width: `${((batch.done_count || batch.validated_count || 0) / batch.total_count) * 100}%` }}
                        ></div>
                      </div>
                    )}
                  </td>
                  <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(batch.created_at).toLocaleDateString('fr-FR')}
                  </td>
                  <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm">
                    <div className="flex flex-col gap-2">
                      <button
                        onClick={() => handleViewDetails(batch.id)}
                        className="text-red-600 hover:text-red-900 text-left"
                      >
                        Détails
                      </button>
                  {!isReadOnly && batch.status === 'failed' && (
                    <button
                      onClick={() => handleEscalateClick(batch.id)}
                      className="text-red-600 hover:text-red-900 text-left"
                    >
                      Escalader
                    </button>
                  )}
                  {!isReadOnly && batch.status === 'escalated' && !batch.escalated_verif && (
                    <button
                      onClick={() => handleVerifyClick(batch.id)}
                      className="text-green-600 hover:text-green-900 text-left"
                    >
                      Vérifier
                    </button>
                  )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="block md:hidden">
          {batches.map((batch) => (
            <div key={batch.id} className="border-b border-gray-200 p-4 last:border-b-0">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="text-sm font-semibold text-gray-900">Lot #{batch.id}</div>
                  <div className="text-xs text-gray-500">Lot N° {batch.batch_number}</div>
                </div>
                {getStatusBadge(batch.status, batch.is_survey_enabled)}
              </div>
              
              <div className="space-y-2 text-sm">
                <div>
                  <span className="text-gray-600 font-medium">{batch.supervisor_id ? 'Prestataire:' : 'Enquêteur:'}</span>
                  <div className="text-gray-900">{batch.interviewer?.name || batch.supervisor?.name || 'N/A'}</div>
                  <div className="text-xs text-gray-500">Ref: {batch.interviewer?.ref || batch.supervisor?.ref || 'N/A'}</div>
                </div>
                
                {(batch.survey || (batch.assignments && batch.assignments.length > 0 && batch.assignments[0]?.survey)) && (
                  <div>
                    <span className="text-gray-600 font-medium">Enquête:</span>
                    <div className="text-gray-900">
                      {batch.survey ? (batch.survey.title || batch.survey.name || 'N/A') : 
                       batch.assignments && batch.assignments[0]?.survey ? 
                       (batch.assignments[0].survey.title || batch.assignments[0].survey.name || 'N/A') : 'N/A'}
                    </div>
                    <div className="text-xs text-gray-500">
                      ID: {batch.survey ? (batch.survey.limesurvey_id || batch.survey.id || 'N/A') :
                           batch.assignments && batch.assignments[0]?.survey ?
                           (batch.assignments[0].survey.limesurvey_id || batch.assignments[0].survey.id || 'N/A') : 'N/A'}
                    </div>
                  </div>
                )}
                
                <div>
                  <span className="text-gray-600 font-medium">Contrôleurs:</span>
                  {batch.assigned_controllers && batch.assigned_controllers.length > 0 ? (
                    <div className="space-y-1 mt-1">
                      {batch.assigned_controllers.map((qc, index) => (
                        <div key={qc.id || index} className="text-xs">
                          <div className="text-gray-900">{qc.controller?.name || 'N/A'}</div>
                          <div className="text-gray-500">Ref: {qc.controller?.ref || 'N/A'}</div>
                          {qc.assigned_assignments_count > 0 && (
                            <div className="text-blue-600">{qc.assigned_assignments_count} questionnaire(s)</div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : batch.assigned_controller ? (
                    <div className="text-xs mt-1">
                      <div className="text-gray-900">{batch.assigned_controller.name}</div>
                      <div className="text-gray-500">Ref: {batch.assigned_controller.ref}</div>
                    </div>
                  ) : (
                    <span className="text-gray-400 italic text-xs">Non assigné</span>
                  )}
                </div>
                
                <div>
                  <span className="text-gray-600 font-medium">Résultats:</span>
                  <div className="text-green-600 font-medium text-xs mt-1">
                    {batch.validated_count || 0} / {batch.total_count} validés
                  </div>
                  {batch.total_count > 0 && (
                    <div className="mt-1 w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-red-600 h-2 rounded-full transition-all" 
                        style={{ width: `${((batch.done_count || batch.validated_count || 0) / batch.total_count) * 100}%` }}
                      ></div>
                    </div>
                  )}
                </div>
                
                <div>
                  <span className="text-gray-600 font-medium">Date:</span>
                  <div className="text-gray-900 text-xs">{new Date(batch.created_at).toLocaleDateString('fr-FR')}</div>
                </div>
                
                <div className="pt-2 border-t border-gray-200 flex flex-col gap-2">
                  <button
                    onClick={() => handleViewDetails(batch.id)}
                    className="w-full px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors text-sm"
                  >
                    Détails
                  </button>
                  {!isReadOnly && batch.status === 'failed' && (
                    <button
                      onClick={() => handleEscalateClick(batch.id)}
                      className="w-full px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors text-sm"
                    >
                      Escalader
                    </button>
                  )}
                  {!isReadOnly && batch.status === 'escalated' && !batch.escalated_verif && (
                    <button
                      onClick={() => handleVerifyClick(batch.id)}
                      className="w-full px-4 py-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors text-sm"
                    >
                      Vérifier
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {batches.length === 0 && !loading && (
          <div className="text-center py-12 text-gray-500">
            Aucun lot trouvé
          </div>
        )}
        {loading && (
          <div className="px-4 sm:px-6 py-12 text-center">
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mb-4"></div>
              <span className="text-gray-600">Chargement...</span>
            </div>
          </div>
        )}
      </div>

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-4">
          <div className="text-xs sm:text-sm text-gray-700 text-center sm:text-left">
            <span>
              Affichage de {((currentPage - 1) * batchesPerPage) + 1} à {Math.min(currentPage * batchesPerPage, totalBatches)} sur {totalBatches} lots
            </span>
          </div>
          
          <div className="flex items-center space-x-2 flex-wrap justify-center">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <FontAwesomeIcon icon={faChevronLeft} className="w-4 h-4" />
            </button>
            
            <div className="flex space-x-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-lg transition-colors ${
                      currentPage === pageNum
                        ? 'bg-red-600 text-white'
                        : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <FontAwesomeIcon icon={faChevronRight} className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Batch Details Modal */}
      {selectedBatch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full m-4 p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-xl font-bold text-gray-900">
                Détails du Lot #{selectedBatch.id}
              </h2>
              <button
                onClick={() => setSelectedBatch(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <div>
                <p className="text-sm text-gray-500">{selectedBatch.supervisor_id ? 'Prestataire' : 'Enquêteur'}</p>
                <p className="font-medium">{selectedBatch.interviewer?.name || selectedBatch.supervisor?.name || 'N/A'}</p>
                <p className="text-xs text-gray-500">Ref: {selectedBatch.interviewer?.ref || selectedBatch.supervisor?.ref || 'N/A'}</p>
                {selectedBatch.interviewer?.supervisor && (
                  <div className="mt-1 text-xs text-gray-500">
                    Prestataire: <span className="font-medium text-gray-700">{selectedBatch.interviewer.supervisor.name}</span> ({selectedBatch.interviewer.supervisor.ref})
                  </div>
                )}
              </div>
              <div>
                <p className="text-sm text-gray-500">Statut</p>
                {getStatusBadge(selectedBatch.status)}
              </div>
              <div>
                <p className="text-sm text-gray-500">Résultat</p>
                <p className="font-medium">
                  {selectedBatch.validated_count} / {selectedBatch.total_count} validés
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Créé le</p>
                <p className="font-medium">
                  {new Date(selectedBatch.created_at).toLocaleString('fr-FR')}
                </p>
              </div>
              {selectedBatch.assigned_controllers && selectedBatch.assigned_controllers.length > 0 ? (
                <div>
                  <p className="text-sm text-gray-500 mb-2">Contrôleurs Assignés</p>
                  <div className="space-y-2">
                    {selectedBatch.assigned_controllers.map((qc, index) => (
                      <div key={qc.id || index} className="bg-gray-50 p-2 rounded">
                        <p className="font-medium text-sm">
                          {qc.controller?.name || 'N/A'} ({qc.controller?.ref || 'N/A'})
                        </p>
                        {qc.assigned_assignments_count > 0 && (
                          <p className="text-xs text-blue-600">
                            {qc.assigned_assignments_count} questionnaire(s) initialement assigné(s)
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                  {selectedBatch.assigned_at && (
                    <p className="text-xs text-gray-400 mt-2">
                      Assigné le: {new Date(selectedBatch.assigned_at).toLocaleString('fr-FR')}
                    </p>
                  )}
                </div>
              ) : selectedBatch.assigned_controller ? (
                <div>
                  <p className="text-sm text-gray-500">Contrôleur Assigné</p>
                  <p className="font-medium">
                    {selectedBatch.assigned_controller.name} ({selectedBatch.assigned_controller.ref})
                  </p>
                  {selectedBatch.assigned_at && (
                    <p className="text-xs text-gray-400">
                      Assigné le: {new Date(selectedBatch.assigned_at).toLocaleString('fr-FR')}
                    </p>
                  )}
                </div>
              ) : (
                <div>
                  <p className="text-sm text-gray-500">Contrôleurs Assignés</p>
                  <p className="font-medium text-gray-400 italic">Non assigné</p>
                </div>
              )}
            </div>

            {selectedBatch.assignments && selectedBatch.assignments.length > 0 && (
              <div>
                <h3 className="font-semibold mb-3">Affectations ({selectedBatch.assignments.length})</h3>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {selectedBatch.assignments.map((assignment) => {
                    const latestCheck = assignment.checks && assignment.checks.length > 0 
                      ? assignment.checks.sort((a, b) => new Date(b.checked_at) - new Date(a.checked_at))[0]
                      : null;
                    
                    return (
                      <div key={assignment.id} className="border rounded p-3 bg-gray-50">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            {(() => {
                              const sub = assignment.submission || assignment.f2f_submission;
                              const responseId = sub?.response_id || assignment.submission_id || assignment.f2f_submission_id;
                              return (
                                <>
                                  <div className="text-sm font-medium text-gray-900">
                                    Soumission #{responseId}
                                  </div>
                                  <div className="text-xs text-gray-500 mt-1">
                                    Statut: {assignment.status === 'done' ? 'Terminé' :
                                             assignment.status === 'in_progress' ? 'En cours' :
                                             'En attente'}
                                  </div>
                                  {sub && assignment.survey && (
                                    <div className="flex gap-3 mt-2">
                                      <a
                                        href={`https://defineresearch.online/index.php/admin/dataentry/sa/editdata/subaction/edit/surveyId/${assignment.survey.limesurvey_id}/id/${sub.response_id}/browseLang`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-xs text-red-600 hover:text-red-800 flex items-center gap-1"
                                      >
                                        <FontAwesomeIcon icon={faFileAlt} />
                                        Questionnaire
                                      </a>
                                      {!assignment.is_f2f && (
                                        <a
                                          href="https://192.168.1.253/index.php?menu=monitoring"
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-xs text-red-600 hover:text-red-800 flex items-center gap-1"
                                        >
                                          <FontAwesomeIcon icon={faVolumeUp} />
                                          Audio
                                        </a>
                                      )}
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                          <div>
                            {assignment.controller ? (
                              <div>
                                <div className="text-xs text-gray-500">Contrôlé par:</div>
                                <div className="text-sm font-medium text-gray-900">
                                  {assignment.controller.name}
                                </div>
                                <div className="text-xs text-gray-500">
                                  Ref: {assignment.controller.ref}
                                </div>
                              </div>
                            ) : (
                              <div className="text-xs text-gray-400 italic">Non assigné</div>
                            )}
                          </div>
                          {latestCheck && (
                            <>
                              <div>
                                <div className="text-xs text-gray-500">Note:</div>
                                <div className="text-sm font-medium text-gray-900">
                                  {latestCheck.rating}/5 ⭐
                                </div>
                              </div>
                              <div>
                                <div className="text-xs text-gray-500">Action:</div>
                                <div className="text-sm">
                                  <span className={`px-2 py-1 rounded-full text-xs ${
                                    latestCheck.action === 'validate' ? 'bg-green-100 text-green-800' :
                                    latestCheck.action === 'reject' ? 'bg-red-100 text-red-800' :
                                    'bg-yellow-100 text-yellow-800'
                                  }`}>
                                    {latestCheck.action === 'validate' ? 'Validé' :
                                     latestCheck.action === 'reject' ? 'Rejeté' :
                                     'En attente'}
                                  </span>
                                  {latestCheck.correction_applied && (
                                    <span className="ml-2 text-xs text-orange-600">
                                      (Correction appliquée)
                                    </span>
                                  )}
                                </div>
                              </div>
                            </>
                          )}
                          {latestCheck && latestCheck.checked_at && (
                            <div className="col-span-2">
                              <div className="text-xs text-gray-400">
                                Contrôlé le: {new Date(latestCheck.checked_at).toLocaleString('fr-FR')}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Export Error Modal */}
      {exportError && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full m-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Erreur d'exportation</h3>
              <button
                onClick={() => setExportError(null)}
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <p className="text-gray-700 flex-1">{exportError}</p>
              </div>
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => setExportError(null)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Escalate Confirmation Modal */}
      <ConfirmationModal
        isOpen={showEscalateModal}
        onClose={() => {
          setShowEscalateModal(false);
          setBatchToAction(null);
        }}
        onConfirm={handleEscalate}
        title="Escalader le lot"
        message="Êtes-vous sûr de vouloir escalader ce lot au niveau supérieur ?"
        confirmText="Escalader"
        cancelText="Annuler"
        confirmColor="bg-red-600 hover:bg-red-700"
        loading={processingAction}
      />

      {/* Verify Confirmation Modal */}
      <ConfirmationModal
        isOpen={showVerifyModal}
        onClose={() => {
          setShowVerifyModal(false);
          setBatchToAction(null);
        }}
        onConfirm={handleVerify}
        title="Vérifier le lot"
        message="Êtes-vous sûr de vouloir marquer ce lot escaladé comme vérifié ?"
        confirmText="Vérifier"
        cancelText="Annuler"
        confirmColor="bg-green-600 hover:bg-green-700"
        loading={processingAction}
      />

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          setSuccessMessage('');
        }}
        title="Opération terminée"
        message={successMessage}
      />
    </div>
  );
};

export default QualityBatchesList;

